import { getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { logger } from '@/core/logging/logger';
import {
  getStoredOrganizationCode,
  setStoredOrganizationCode,
} from '@/shared/services/organization/organizationCodeStorage';
import { getOrganization } from './organizationStep1.service';
import {
  applyOrganizationTheme,
  getOrganizationLoginImage,
} from '../../step-2-site-configuration/api/siteConfiguration.service';
import type { OrganizationRecord } from '../models';

/**
 * The login bootstrap from organization-setup-step1-apiflow.md section 2.
 *
 * Runs once per session, before the wizard is ever opened, and is what decides
 * whether Save & Next will POST or PUT:
 *
 *   GET /users/me -> store organizationCode (ALWAYS, null included)
 *     code present -> GET /organizations/{code} -> hydrate
 *     code null    -> skip the GET entirely
 *
 * The auth saga owns the /users/me call already, so this takes the profile it
 * fetched rather than calling it a second time.
 */

/**
 * The record fetched at login, held until the wizard collects it.
 *
 * Consume-once on purpose: the wizard takes it for its first paint, and every
 * later read (Back from step 2) goes to the network, so the form can never
 * show a stale organization.
 */
let hydrated: OrganizationRecord | null = null;

export function cacheBootstrappedOrganization(record: OrganizationRecord | null): void {
  hydrated = record;
}

/** Returns the login-bootstrapped record once, then forgets it. */
export function takeBootstrappedOrganization(): OrganizationRecord | null {
  const record = hydrated;
  hydrated = null;
  return record;
}

export interface OrganizationBootstrapOptions {
  /**
   * Whether to also fetch the organization for the wizard's first paint.
   *
   * False for a tenant Admin, who has no Organization Setup screen: the record would be read and never
   * looked at. Storing the code (section 2.1) is **not** optional for them, though — it is how every screen
   * that works "within your organization" knows which one that is, and skipping the whole bootstrap for
   * Admins left the Masters with no session organization at all.
   */
  readonly hydrate?: boolean;
}

/**
 * Section 2.1 + 2.2. Never throws: a failed bootstrap must not fail the login
 * that triggered it -- the wizard falls back to fetching for itself.
 */
export async function bootstrapOrganizationSession(
  organizationCode: string | null | undefined,
  options: OrganizationBootstrapOptions = {},
): Promise<void> {
  // Section 2.1: write even when null, so "no organization yet" is
  // distinguishable from "not checked yet".
  setStoredOrganizationCode(organizationCode ?? null);
  hydrated = null;

  const stored = getStoredOrganizationCode();

  /*
    The organization's theme is stored per session start, not per browser: the
    public /organizationloginimage payload carries the osp_configurations
    document, so sign-in is where a fresh browser learns which palette the
    organization chose. With no code there is no organization to ask about yet,
    and the login page's own call (which falls back to the platform default) is
    the last word.

    Never throws: a theme that cannot be fetched must not fail the sign-in it
    rides along with -- the current theme simply stays.
  */
  if (stored !== null) {
    try {
      applyOrganizationTheme(await getOrganizationLoginImage(stored));
    } catch (error: unknown) {
      logger.warn(
        'Organization theme fetch failed; keeping the applied theme',
        getErrorMessage(normalizeError(error)),
      );
    }
  }

  // Section 2.2's skip condition, stated as the document states it.
  if (stored === null) return;

  /* Nothing is going to read the record; the stored code above was the whole point of the call. */
  if (options.hydrate === false) return;

  try {
    /*
      `background`: the user did not ask for this, so a refusal must stay here. Reading the organization
      needs `Organization:READ`, which most roles do not hold — an Initiator holds Dashboard, RFQ and
      Supplier grants only — and the global 403 handler would navigate the whole app to /unauthorized,
      making a normal sign-in end on "You do not have access to that page". The catch below is the
      intended response, and it cannot run before the navigation unless the request opts out of it.
    */
    hydrated = await getOrganization(stored, { background: true });
  } catch (error: unknown) {
    logger.warn(
      'Organization bootstrap failed; the wizard will fetch it again',
      getErrorMessage(normalizeError(error)),
    );
  }
}
